import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
} from "react";
import { useQuery } from "react-query";
import { useAuthContext } from "./AuthContext";
import { useTeamSocketPresence } from "../hooks/useTeamSocketPresence";
import { getHumanOperators } from "../api/client";
import { getTeamPresenceKind } from "../utils/teamPresence";
import { hasActiveTenantWorkspaceContext } from "../utils/partnerPortal";

const TeamPresenceContext = createContext(null);

function useTeamUserPresenceState() {
  const { userData } = useAuthContext();
  const { tabsByUserId } = useTeamSocketPresence(userData);
  const tenantActive = hasActiveTenantWorkspaceContext();

  const { data, isLoading } = useQuery(
    ["teamHumanOperators"],
    () => getHumanOperators(200),
    {
      enabled: tenantActive,
      staleTime: 20_000,
      refetchOnWindowFocus: true,
      refetchInterval: tenantActive ? 60_000 : false,
    }
  );

  const operators = data?.operators || [];

  const operatorsById = useMemo(() => {
    const map = new Map();
    for (const u of operators) {
      const id = Number(u?.id);
      if (Number.isFinite(id) && id > 0) map.set(id, u);
    }
    return map;
  }, [operators]);

  const getPresence = useCallback(
    (userId) => {
      const uid = Number(userId);
      if (!Number.isFinite(uid) || uid <= 0) {
        return { kind: "offline", socketTabs: 0, operator: null };
      }
      const operator = operatorsById.get(uid) || null;
      const socketTabs = tabsByUserId.get(uid) || 0;
      const kind = getTeamPresenceKind(
        operator
          ? {
              data_logout: operator.data_logout,
              data_ultimo_acesso: operator.data_ultimo_acesso,
            }
          : {},
        socketTabs
      );
      return { kind, socketTabs, operator };
    },
    [operatorsById, tabsByUserId]
  );

  const grouped = useMemo(() => {
    const online = [];
    const away = [];
    const offline = [];
    for (const u of operators) {
      const uid = Number(u.id);
      const socketTabs = tabsByUserId.get(uid) || 0;
      const presenceKind = getTeamPresenceKind(u, socketTabs);
      const entry = { ...u, presenceKind, socketTabs };
      if (presenceKind === "online") online.push(entry);
      else if (presenceKind === "away") away.push(entry);
      else offline.push(entry);
    }
    const byName = (a, b) =>
      String(a.nome || "").localeCompare(String(b.nome || ""), undefined, {
        sensitivity: "base",
      });
    online.sort(byName);
    away.sort(byName);
    offline.sort(byName);
    return { online, away, offline };
  }, [operators, tabsByUserId]);

  return {
    operators,
    operatorsById,
    getPresence,
    grouped,
    tabsByUserId,
    isLoading,
  };
}

export function TeamPresenceProvider({ children }) {
  const value = useTeamUserPresenceState();
  return (
    <TeamPresenceContext.Provider value={value}>{children}</TeamPresenceContext.Provider>
  );
}

export function useTeamPresence() {
  const ctx = useContext(TeamPresenceContext);
  if (!ctx) {
    throw new Error("useTeamPresence must be used within TeamPresenceProvider");
  }
  return ctx;
}

export function useTeamPresenceOptional() {
  return useContext(TeamPresenceContext);
}
